import { inject, Injectable, NgZone } from '@angular/core';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { environment } from '../../environments/environment';
import { ClientService } from './client.service';
import { AuthService } from './auth.service';
import { SessionGuardService } from './session-guard.service';
import { NeedsConnectionGuard } from './needs-connection';

export interface AISettings {
  dashboardMode: 'auto' | 'off';
}

const AI_SETTINGS_DEFAULTS: AISettings = {
  dashboardMode: 'auto',
};

@Injectable({
  providedIn: 'root',
})
export class AIService {
  private zone = inject(NgZone);
  private firestore = inject(Firestore);
  private clientService = inject(ClientService);
  private authService = inject(AuthService);
  private sessionGuard = inject(SessionGuardService);
  private needsConnection = inject(NeedsConnectionGuard);

  // ── Firestore config ──────────────────────────────────────────────────────

  async getSettings(): Promise<AISettings> {
    const clientId = this.clientService.selectedClientId;
    const ref = doc(this.firestore, `clients/${clientId}/AI/settings`);
    const snap = await getDoc(ref);
    return snap.exists()
      ? { ...AI_SETTINGS_DEFAULTS, ...(snap.data() as Partial<AISettings>) }
      : { ...AI_SETTINGS_DEFAULTS };
  }

  async saveSettings(settings: Partial<AISettings>): Promise<void> {
    const clientId = this.clientService.selectedClientId;
    const ref = doc(this.firestore, `clients/${clientId}/AI/settings`);
    await setDoc(ref, settings, { merge: true });
  }

  // ── SSE: main query execution ─────────────────────────────────────────────

  streamExecute(
    body: object,
    onProgress: (step: string, message: string) => void,
    onResult: (data: any) => void,
    onEmpty: (query: string, message: string) => void,
    onError: (message: string) => void,
    signal: AbortSignal,
    onSuggestions?: (questions: string[]) => void
  ): void {
    this.stream(`${environment.functionsBaseUrl}/aiExecute`, body, {
      progress: (p) => onProgress(p.step, p.message),
      result: (p) => onResult(p),
      empty_result: (p) => onEmpty(p.query, p.message),
      error: (p) => onError(p.message),
      suggestions: (p) => onSuggestions?.(p.questions),
    }, signal);
  }

  // ── SSE: dashboard (expanded report) ─────────────────────────────────────

  streamDashboard(
    body: object,
    onProgress: (step: string, message: string) => void,
    onReady: (html: string) => void,
    onError: (message: string) => void,
    signal: AbortSignal
  ): void {
    this.stream(`${environment.functionsBaseUrl}/aiDashboard`, body, {
      progress: (p) => onProgress(p.step, p.message),
      dashboard_ready: (p) => onReady(p.html),
      error: (p) => onError(p.message),
    }, signal);
  }

  // ── Generic SSE reader ────────────────────────────────────────────────────

  private async stream(
    url: string,
    body: object,
    handlers: Record<string, (parsed: any) => void>,
    signal: AbortSignal
  ): Promise<void> {
    // An AI call is never queued; in Offline mode it reports the shared sentence through the caller's
    // own error handler, so it shows once, where that call's errors already show.
    const stop = this.needsConnection.stop();
    if (stop) {
      this.zone.run(() => handlers['error']?.({ message: stop }));
      return;
    }
    const token = await this.authService.getAuthToken();
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'X-Session-Id': this.sessionGuard.getSessionId(),
      },
      body: JSON.stringify(body),
      signal,
    }).then(async (res) => {
      if (!res.ok || !res.body) {
        handlers['error']?.({ message: `HTTP ${res.status}` });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          let eventType = 'message';
          let dataLine = '';
          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataLine = line.slice(6).trim();
          }
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine);
            const handler = handlers[eventType];
            if (handler) this.zone.run(() => handler(parsed));
          } catch { /* malformed SSE event — skip */ }
        }
      }
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        handlers['error']?.({ message: err.message ?? 'Unknown error' });
      }
    });
  }
}
